import axios from 'axios';
import store from '../redux/store';
import i18n from '../localization/i18next';
import keycloak from '../services/authentication/Keycloak';
import clients from '../config';
import { expireSession } from '../services/authentication/SessionService';
import { build_auth_app_login_url, should_bootstrap_keycloak } from '../services/authentication/auth_urls';
import { read_bearer_authorization, read_is_token_current } from '../services/authentication/session_tokens';

const token_refreshes = new WeakMap();
const auth_redirects = new WeakSet();
const auth_retry_flag = '_godfatherAuthRetried';

export const read_request_authorization = (request, token) => {
    const authorization = read_bearer_authorization(token);
    const legacyBackend = `${clients.ERISED_BACKEND_URL || ''}`.replace(/\/+$/, '');
    if (legacyBackend && `${request.url || ''}`.startsWith(`${legacyBackend}/`)) {
        return authorization.replace(/^Bearer\s+/i, '');
    }
    return authorization;
};

const read_route_language = (activeWindow = window) => {
    const routeLanguage = String(activeWindow?.location?.pathname || '').split('/')[1];

    return routeLanguage || i18n.language || 'en';
};

const refresh_keycloak_access_token = async (activeKeycloak, minimumValiditySeconds = 30) => {
    if (!activeKeycloak?.authenticated || typeof activeKeycloak?.updateToken !== 'function') {
        return activeKeycloak?.token || '';
    }

    const pendingRefresh = token_refreshes.get(activeKeycloak);

    if (pendingRefresh) {
        return pendingRefresh;
    }

    const refresh = Promise.resolve()
        .then(() => activeKeycloak.updateToken(minimumValiditySeconds))
        .then(() => activeKeycloak.token || '')
        .finally(() => {
            if (token_refreshes.get(activeKeycloak) === refresh) {
                token_refreshes.delete(activeKeycloak);
            }
        });

    token_refreshes.set(activeKeycloak, refresh);

    return refresh;
};

const remove_request_authorization = (request = {}) => {
    if (typeof request?.headers?.delete === 'function') {
        request.headers.delete('Authorization');
        return;
    }

    if (request.headers) {
        delete request.headers.Authorization;
        delete request.headers.authorization;
    }
};

export const prepare_authenticated_request = async (request = {}, options = {}) => {
    const activeStore = options.storeClient || store;
    const activeKeycloak = options.keycloakClient || keycloak;
    let keycloakToken = activeKeycloak?.token || '';

    try {
        keycloakToken = await refresh_keycloak_access_token(activeKeycloak);
    } catch (error) {
        keycloakToken = activeKeycloak?.token || '';
    }

    const persistedToken = activeStore.getState()?.user?.token || '';
    const requiresLiveKeycloak = options.requiresLiveKeycloak ?? should_bootstrap_keycloak();
    const token = read_is_token_current(keycloakToken)
        ? keycloakToken
        : !requiresLiveKeycloak && read_is_token_current(persistedToken)
            ? persistedToken
            : '';
    const authorization = read_request_authorization(request, token);

    request.headers = request.headers || {};
    request.headers.Lang = i18n.language || 'en';

    if (!authorization) {
        remove_request_authorization(request);
        return request;
    }

    request.headers.Authorization = authorization;

    if (token === keycloakToken && token !== persistedToken) {
        activeStore.dispatch({
            type: 'SET_TOKEN',
            payload: token
        });
    }

    return request;
};

export const read_is_unauthorized_response = (error = {}) => {
    const httpStatus = Number(error?.response?.status);
    const responseCode = Number(error?.response?.data?.code);

    return httpStatus === 401 || responseCode === 401;
};

export const recover_unauthorized_session = (error = {}, options = {}) => {
    if (!read_is_unauthorized_response(error)) {
        return false;
    }

    const activeWindow = options.windowClient || window;
    const activeKeycloak = options.keycloakClient || keycloak;
    const clearSession = options.expireSessionClient || expireSession;
    const buildLoginUrl = options.buildLoginUrl || build_auth_app_login_url;
    const redirectUri = activeWindow.location.href;
    const hasLiveKeycloakSession = Boolean(
        activeKeycloak?.authenticated
        && read_is_token_current(activeKeycloak?.token || '')
    );

    if (hasLiveKeycloakSession) {
        return false;
    }

    if (auth_redirects.has(activeWindow)) {
        return true;
    }

    auth_redirects.add(activeWindow);

    try {
        activeKeycloak?.clearToken?.();
    } catch (error) {
    }

    clearSession();

    if (typeof activeKeycloak?.login === 'function') {
        activeKeycloak.login({ redirectUri });
        return true;
    }

    activeWindow.location.assign(
        buildLoginUrl(read_route_language(activeWindow), redirectUri)
    );

    return true;
};

const save_refreshed_token = (activeStore, token) => {
    activeStore.dispatch({
        type: 'SET_TOKEN',
        payload: token
    });
};

export const retry_unauthorized_request = async (error = {}, options = {}) => {
    if (!read_is_unauthorized_response(error)) {
        return {
            retried: false
        };
    }

    const request = error?.config || {};

    if (!request.url || request[auth_retry_flag]) {
        return {
            retried: false
        };
    }

    const axiosClient = options.axiosClient || axios;
    const activeStore = options.storeClient || store;
    const activeKeycloak = options.keycloakClient || keycloak;

    try {
        const refreshedToken = await refresh_keycloak_access_token(activeKeycloak, -1);
        const authorization = read_request_authorization(request, refreshedToken);

        if (!activeKeycloak?.authenticated || !read_is_token_current(refreshedToken) || !authorization) {
            return {
                retried: false
            };
        }

        save_refreshed_token(activeStore, refreshedToken);

        return {
            retried: true,
            response: axiosClient({
                ...request,
                [auth_retry_flag]: true,
                headers: {
                    ...request.headers,
                    Authorization: authorization
                }
            })
        };
    } catch (refreshError) {
        return {
            retried: false
        };
    }
};

export const jwtReqInterceptor = (options = {}) => {
    const axiosClient = options.axiosClient || axios;

    return axiosClient.interceptors.request.use((request) => {
        return prepare_authenticated_request(request, options);
    });
};

export const jwtResInterceptor = (options = {}) => {
    const axiosClient = options.axiosClient || axios;

    return axiosClient.interceptors.response.use(
        (response) => response,
        async (error) => {
            const retryResult = await retry_unauthorized_request(error, options);

            if (retryResult.retried) {
                return retryResult.response;
            }

            if (!error.config?.skipAuthRedirect) recover_unauthorized_session(error, options);

            return Promise.reject(error);
        }
    );
};
